import React from 'react';

import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import CirclesRating, {
  FilledCircleColor
} from '../../../circles-rating/circles-rating';
import StoppingDistanceChart from '../../../stopping-distance-chart/stopping-distance-chart';

import styles from './tire-comparison.scss';

interface TireComparisonProps {
  currentDistance: number;
  currentRating?: number;
  currentWornDistance?: number;
  isDry?: boolean;
  isLT?: boolean;
  isWet?: boolean;
  recommendedDistance: number;
  recommendedProductLabel: string;
  recommendedProductUrl: string;
  recommendedRating?: number;
  recommendedWornDistance?: number;
}

function TireComparison(props: TireComparisonProps) {
  const {
    currentDistance,
    currentRating,
    currentWornDistance,
    isDry,
    isLT,
    isWet,
    recommendedDistance,
    recommendedProductLabel,
    recommendedProductUrl,
    recommendedRating,
    recommendedWornDistance
  } = props;
  const scaleMax = isLT ? 350 : 250;
  const scaleMin = 0;

  const renderBaseline = () => (
    <>
      <div className={styles['chart-baseline']} />
      <div className={styles['chart-baseline-overflow']} />
    </>
  );

  const renderTicks = (_, scaleMax, scaleMin) => (
    <>
      {renderBaseline()}
      <span className={styles.tick} style={{ right: '-2px' }}>
        {scaleMax}
      </span>
      <span className={styles.tick}>{scaleMin}</span>
    </>
  );

  return (
    <div>
      {isDry && (
        <>
          <Typography className={styles['heading']} size="small">
            Current tire
            {typeof currentRating === 'number' && (
              <div className={styles['circles-rating-container']}>
                <CirclesRating
                  filledCircleColor={FilledCircleColor.GOLD}
                  rating={Number(currentRating)}
                />
                {currentRating.toFixed(1)}
              </div>
            )}
          </Typography>
          <div className={styles['chart-row-wrap']}>
            <div className={styles['chart-row__dry-tread']}>
              <StoppingDistanceChart
                className={styles['chart']}
                renderTicks={renderTicks}
                scaleMax={scaleMax}
                scaleMin={scaleMin}
                value={currentDistance}
              />
            </div>
          </div>
          {Boolean(recommendedDistance && recommendedRating) && (
            <>
              <Typography
                className={styles['recommended-heading']}
                size="small"
              >
                <div>
                  Best tire
                  {Boolean(recommendedProductUrl) && (
                    <>
                      {' - '}
                      <Link
                        className={styles['link']}
                        to={recommendedProductUrl}
                      >
                        {recommendedProductLabel}
                      </Link>
                    </>
                  )}
                </div>
                {typeof recommendedRating === 'number' && (
                  <div className={styles['circles-rating-container']}>
                    <CirclesRating
                      filledCircleColor={FilledCircleColor.GOLD}
                      rating={recommendedRating}
                    />
                    {recommendedRating.toFixed(1)}
                  </div>
                )}
              </Typography>
              <div className={styles['chart-row-wrap']}>
                <div className={styles['chart-row__dry-tread']}>
                  <StoppingDistanceChart
                    className={styles['chart']}
                    renderTicks={renderTicks}
                    scaleMax={scaleMax}
                    scaleMin={scaleMin}
                    value={recommendedDistance}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {isWet && (
        <>
          <Typography className={styles['heading']} size="small">
            Current tire
            {typeof currentRating === 'number' && (
              <div className={styles['circles-rating-container']}>
                <CirclesRating
                  filledCircleColor={FilledCircleColor.BLUE}
                  rating={currentRating}
                />
                {currentRating.toFixed(1)}
              </div>
            )}
          </Typography>
          <div className={styles['chart-row-wrap']}>
            <div className={styles['chart-row__new-tread']}>
              <span className={styles['wet-tread-type']}>New</span>
              <StoppingDistanceChart
                className={styles['chart']}
                renderTicks={renderBaseline}
                scaleMax={scaleMax}
                scaleMin={scaleMin}
                value={currentDistance}
              />
            </div>
            {Number(currentWornDistance) > 0 && (
              <div className={styles['chart-row__worn-tread']}>
                <span className={styles['wet-tread-type']}>Worn</span>
                <StoppingDistanceChart
                  className={styles['chart']}
                  renderTicks={renderTicks}
                  scaleMax={scaleMax}
                  scaleMin={scaleMin}
                  value={Number(currentWornDistance)}
                />
              </div>
            )}
          </div>
          {Boolean(
            recommendedDistance && recommendedRating && recommendedWornDistance
          ) && (
            <>
              <Typography
                className={styles['recommended-heading']}
                size="small"
              >
                <div>
                  Best tire
                  {Boolean(recommendedProductUrl) && (
                    <>
                      {' - '}
                      <Link
                        className={styles['link']}
                        to={recommendedProductUrl}
                      >
                        {recommendedProductLabel}
                      </Link>
                    </>
                  )}
                </div>
                {typeof recommendedRating === 'number' && (
                  <div className={styles['circles-rating-container']}>
                    <CirclesRating
                      filledCircleColor={FilledCircleColor.BLUE}
                      rating={recommendedRating}
                    />
                    {recommendedRating.toFixed(1)}
                  </div>
                )}
              </Typography>
              <div className={styles['chart-row-wrap']}>
                <div className={styles['chart-row__new-tread']}>
                  <span className={styles['wet-tread-type']}>New</span>
                  <StoppingDistanceChart
                    className={styles['chart']}
                    renderTicks={renderBaseline}
                    scaleMax={scaleMax}
                    scaleMin={scaleMin}
                    value={recommendedDistance}
                  />
                </div>
                {Number(recommendedWornDistance) > 0 && (
                  <div className={styles['chart-row__worn-tread']}>
                    <span className={styles['wet-tread-type']}>Worn</span>
                    <StoppingDistanceChart
                      className={styles['chart']}
                      renderTicks={renderTicks}
                      scaleMax={scaleMax}
                      scaleMin={scaleMin}
                      value={Number(recommendedWornDistance)}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TireComparison;
